// app/javascript/landing/sections/ImmersiveIntroSection.tsx
import React, { useRef } from "react";
import {
  motion,
  Transition,
  Variants,
  useScroll,
  useTransform,
} from "framer-motion";

const transition: Transition = { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] };
const variants: Variants = {
  hidden: { opacity: 0, y: 20, filter: "blur(6px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0)" },
};

export function ImmersiveIntroSection() {
  const sectionRef = useRef<HTMLElement | null>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Parallax en capas
  const dotsY = useTransform(scrollYProgress, [0, 1], [400, -400]); // muy suave
  const boxY = useTransform(scrollYProgress, [0, 1], [-50, 50]); // medio
  const imgY = useTransform(scrollYProgress, [0, 1], [50, -50]); // fuerte

  return (
    <motion.section
      ref={sectionRef}
      className="x-bg-red-400 overflow-hidden"
      initial="hidden"
      whileInView="visible"
      transition={{ staggerChildren: 0.12 }}
    >
      <div className="relative max-w-6xl mx-auto px-6">
        {/* ====================== */}
        {/*   PUNTOS DECORATIVOS   */}
        {/* ====================== */}
        <motion.svg
          aria-hidden="true"
          viewBox="0 0 320 420"
          style={{ y: dotsY }}
          className="
    pointer-events-none
    hidden md:block
    absolute
    left-0
    -bottom-40
    -translate-x-full
    w-56 h-56 lg:w-72 lg:h-72
    text-purple-2/50
  "
        >
          {Array.from({ length: 4 }).map((_, row) =>
            Array.from({ length: 6 }).map((__, col) => (
              <circle
                key={`${row}-${col}`}
                cx={50 * col + 20} // separación horizontal
                cy={100 * row + 20} // separación vertical
                r={6}
                fill="currentColor"
              />
            ))
          )}
        </motion.svg>

        {/* GRID CONTENIDO PRINCIPAL */}
        <div className="grid gap-16 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-start">
          {/* TEXTO */}
          <motion.div
            variants={variants}
            transition={transition}
            className="space-y-8"
          >
            <div className="flex items-start relative">
              <div className="absolute -left-4 h-full w-1 bg-purple-2 rounded-full" />
              <h2 className="text-purple-2 text-2xl md:text-3xl font-bold leading-snug">
                Una plataforma inmersiva que integra conocimiento, experiencia y
                tecnología en un solo ecosistema
              </h2>
            </div>

            <p className="text-lg md:text-lg text-neutral-700 leading-relaxed">
              Si bien el{" "}
              <strong className="font-semibold">
                84% de las grandes empresas
              </strong>{" "}
              del rubro de la construcción tienen la intención de adoptar nuevas
              tecnologías,{" "}
              <strong className="font-semibold">más del 40% se resiste</strong>{" "}
              porque consideran que su nivel actual de desarrollo tecnológico es
              insuficiente.{" "}
              <span className="text-xs align-baseline">
                (Fuente: McKinsey &amp; Company)
              </span>
            </p>

            <p className="text-lg md:text-lg text-neutral-700 leading-relaxed">
              El Metaverso de la Construcción es una herramienta valiosa para{" "}
              <span className="highlight-marker">
                <strong className="font-semibold">
                  mejorar la eficiencia, reducir la incertidumbre e impulsar la
                  innovación
                </strong>
              </span>{" "}
              dentro de una industria que históricamente se ha resistido a la
              adopción de nuevas y mejores tecnologías.
            </p>
          </motion.div>

          {/* VR CARD */}
          <motion.div
            variants={variants}
            transition={transition}
            className="flex md:justify-end justify-center"
          >
            <div className="relative w-full max-w-md lg:max-w-xl aspect-square">
              {/* Fondo con parallax medio */}
              <motion.div
                aria-hidden="true"
                style={{ y: boxY }}
                className="
                  absolute inset-0
                  rounded-[32px]
                  bg-gradient-to-br
                  from-[#E9E1FF]
                  via-[#D6C4FF]
                  to-[#B69CFF]
                  origin-[100%_40%]
                  scale-[0.7]
                "
              />

              {/* Imagen VR con parallax fuerte */}
              <motion.img
                src="/landing/devices/vr-headset.png"
                alt="Visor y controles de realidad virtual"
                style={{ y: imgY }}
                className="
                  relative z-10
                  w-full h-full
                  object-contain
                "
              />
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}
